import {
  DestroyRef,
  ErrorHandler,
  Injectable,
  makeEnvironmentProviders,
  provideAppInitializer,
  inject,
} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  ResolveEnd,
  Router,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  browserTracingIntegration,
  createErrorHandler,
  getActiveSpan,
  getRootSpan,
  getClient,
  spanToJSON,
  TraceService,
} from '@sentry/angular';
import type { Span } from '@sentry/angular';

/** Normalize SDK-created navigation names without adding a second tracing instrumenter. */
@Injectable({ providedIn: 'root' })
class SentryRoutingNames {
  private routingSpan?: Span;

  constructor() {
    const destroy = inject(DestroyRef);
    const stop = getClient()?.on('spanStart', (span) => {
      const operation = spanToJSON(span).op;
      if (
        operation === 'navigation' ||
        operation === 'pageload' ||
        operation === 'ui.angular.routing'
      ) {
        span.updateName('Angular navigation');
        if (operation === 'ui.angular.routing') {
          span.setAttribute('url', undefined);
          this.routingSpan = span;
        }
      }
    });
    destroy.onDestroy(() => {
      stop?.();
    });
    inject(Router)
      .events.pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event instanceof ResolveEnd) {
          const template = this.routeTemplate(event.state.root);
          this.routingSpan?.updateName(template);
          this.routingSpan?.setAttribute('sentry.source', 'route');
          const active = getActiveSpan();
          const root = active && getRootSpan(active);
          if (
            root &&
            ['navigation', 'pageload'].includes(spanToJSON(root).op ?? '')
          ) {
            root.updateName(template);
            root.setAttribute('sentry.source', 'route');
          }
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          this.routingSpan = undefined;
        }
      });
  }

  private routeTemplate(root: ActivatedRouteSnapshot): string {
    const parts: string[] = [];
    for (let route = root.firstChild; route; route = route.firstChild) {
      const path = route.routeConfig?.path;
      if (path === undefined) return 'Angular navigation';
      if (path) parts.push(path);
    }
    return parts.length ? `/${parts.join('/')}/` : '/';
  }
}

/** Angular catches template errors itself; the browser's global handler is insufficient. */
export function provideSentryAngular() {
  return makeEnvironmentProviders([
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        logErrors: true,
        extractor: (error, defaultExtractor) => {
          // Zone keeps promise metadata on a wrapper for its browser bridge.
          // Report the original rejection so stacks and per-error tags survive.
          if (
            error &&
            typeof error === 'object' &&
            'promise' in error &&
            'rejection' in error &&
            typeof (error as { promise?: { then?: unknown } }).promise?.then ===
              'function'
          ) {
            return defaultExtractor(error.rejection);
          }
          return defaultExtractor(error);
        },
      }),
    },
    { provide: TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(SentryRoutingNames);
      inject(TraceService);
    }),
  ]);
}

/** Give initial/unfinished navigation a safe name before Angular resolves a template. */
export function sentryAngularTracingIntegration() {
  return browserTracingIntegration({
    instrumentNavigation: true,
    beforeStartSpan: (options) => ({ ...options, name: 'Angular navigation' }),
  });
}
